import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { theme } from '@/theme/theme';
import {
  GetPointOfInterestGroupedByWorkpackDocument,
  useUpdateWorkpackMutation,
  WorkpackType,
  WorkpackWbs,
} from '@/__generated__/graphql';
import { NOT_APPLICABLE_LABEL, WBS_FORMAT_FILTER, WORKPACK_TYPE_FORMAT_FILTER } from '@/constants';

type EditWorkpackDialogProps = {
  isOpen: boolean;
  title: string;
  selectedWorkpackId: string;
  selectedWorkpackName: string;
  selectedWorkpackWbs?: WorkpackWbs | null;
  selectedWorkpackType?: WorkpackType | null;
  handleClose: () => void;
};

export const EditWorkpackDialog = ({
  isOpen,
  title,
  selectedWorkpackId,
  selectedWorkpackName,
  selectedWorkpackWbs,
  selectedWorkpackType,
  handleClose,
}: EditWorkpackDialogProps) => {
  const [workpackName, setWorkpackName] = useState<string>(selectedWorkpackName);
  const [workpackWbs, setWorkpackWbs] = useState<WorkpackWbs | typeof NOT_APPLICABLE_LABEL>(
    selectedWorkpackWbs ?? NOT_APPLICABLE_LABEL
  );
  const [workpackType, setWorkpackType] = useState<WorkpackType | typeof NOT_APPLICABLE_LABEL>(
    selectedWorkpackType ?? NOT_APPLICABLE_LABEL
  );
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const structureId = useGetStructureId();

  const [updateWorkpack, { loading }] = useUpdateWorkpackMutation();

  const handleUpdateWorkpack = () => {
    updateWorkpack({
      variables: {
        input: {
          workpackId: selectedWorkpackId,
          name: workpackName,
          wbs: workpackWbs === NOT_APPLICABLE_LABEL ? null : workpackWbs,
          type: workpackType === NOT_APPLICABLE_LABEL ? null : workpackType,
          structureId,
        },
      },
      onCompleted: () => {
        showSnackBar('Workpack has been updated successfully', 'success');
        handleClose();
      },
      onError: (error) => {
        handleClose();
        showSnackBar(`${error.message}`, 'error');
      },
      refetchQueries: [GetPointOfInterestGroupedByWorkpackDocument],
      awaitRefetchQueries: true,
    });
  };

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth sx={{ radius: 2 }}>
      <DialogTitle sx={{ m: 0, p: 2 }}>{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <TextField
          fullWidth
          label="Workpack name"
          variant="standard"
          value={workpackName}
          onChange={(event) => setWorkpackName(event?.target.value)}
        />
        <FormControl fullWidth variant="standard" sx={{ mt: 3 }}>
          <InputLabel id="type-select-label">Workpack Type</InputLabel>
          <Select
            labelId="type-select-label"
            id="type-select"
            value={workpackType}
            label="Workpack Type"
            onChange={(event) =>
              setWorkpackType(event.target.value as WorkpackType | typeof NOT_APPLICABLE_LABEL)
            }
          >
            {[...Object.values(WorkpackType), NOT_APPLICABLE_LABEL].map((option) => (
              <MenuItem key={option} value={option}>
                {WORKPACK_TYPE_FORMAT_FILTER[option as keyof typeof WORKPACK_TYPE_FORMAT_FILTER]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth variant="standard" sx={{ mt: 3 }}>
          <InputLabel id="wbs-select-label">WBS</InputLabel>
          <Select
            labelId="wbs-select-label"
            id="wbs-select"
            value={workpackWbs}
            label="WBS"
            onChange={(event) =>
              setWorkpackWbs(event.target.value as WorkpackWbs | typeof NOT_APPLICABLE_LABEL)
            }
          >
            {[...Object.values(WorkpackWbs), NOT_APPLICABLE_LABEL].map((option) => (
              <MenuItem key={option} value={option}>
                {WBS_FORMAT_FILTER[option as keyof typeof WBS_FORMAT_FILTER]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <Divider />
      <Divider orientation="horizontal" />
      <DialogActions>
        <Button onClick={handleClose} color="primary" size="large">
          Cancel
        </Button>
        <LoadingButton onClick={handleUpdateWorkpack} loading={loading}>
          Update
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
